// 画面の寄せ (2026-10-18)。
//   作者「立ち絵を並べてもらって思ったけど主人公含めて全体的に画面に対して小さいね。」→「全員を同じ比率で大きくしたい」。
//   比較版 (1 / 1.15 / 1.3 / 1.45) を見比べて、 作者の答え「1.3倍がいい」(10-18) = 配布版は 1.3 倍。
//   開発版: ?camZoom=1 で旧の見え方 (等倍)・?camZoom=1.15|1.45 で他の段・Z キーで循環。 ?camZoomFit=0 で「離れても引かない」版。
//
//   やり方 = 世界の層 (背景・床・キャラ・FX) を今までどおり 960x540 に描いてから、 二人を囲む矩形を切り取って画面いっぱいに拡大する。
//   HUD・名乗り・負け画面などはその後に等倍で描くので大きくならない。
//   ⚠️ 当たり・間合い・移動の数値は 1px も変わらない (描いた結果を拡大するだけ)。
//   ⚠️ 戦い以外の画面 (タイトル・会話・選択など) は等倍のまま。
//
//   寄せ方:
//   ・床の高さを画面の同じ位置 (y=440) に保ったまま寄る = 頭の上が詰まり、足元の床の帯は今と同じ見え方。
//   ・横は二人 (と主人公の近くの予告の帯・飛び道具) を囲む範囲の真ん中を追う。
//   ・二人が離れて画面に入りきらない時は、 入るところまで引く (寄せの上限は段の倍率)。 引く時は速く・寄る時はゆっくり。
//   ・背景の絵の外は映さない (切り取る矩形を画面 0..960 / 0..540 の内側に収める)。
//
//   案C (2026-10-18・配布版の既定・作者の答え「入れる」・docs/research/2026-10-18-cam-zoom-bg-resolution.md): 1.3 倍だと上の帯 (y<101.5) がいつも切れ、
//     ステンドグラス・月・迫撃砲の弾・壁に跳び付いたボスの頭が映らない。
//   ・名乗り (開幕) と決着 (勝ち・負け) は等倍で部屋全体を見せる (input.wide)。 戦いが始まったらゆっくり寄る。
//   ・上へ出る物 (input.topY = 映したい一番上の y) が切れそうな時は、 入るところまで引く。 ボスの頭 (input.bossTopY) は補間の途中でも必ず入れる。
//   ・開発版 ?camZoomScene=0 で案C を切る (横だけを見る前の寄せ方)。

import { ARENA } from './balance'

export const CAM_ZOOM_LEVELS = [1, 1.15, 1.3, 1.45] as const
// 配布版の寄せ (作者の答え 10-18「1.3倍がいい」)。
export const CAM_ZOOM_DEFAULT = 1.3

// 二人の外側に取る余白 (ボスの得物・振りの広がりぶん)。
const PAIR_MARGIN_PX = 150
// 余白込みでも入らない時の最後の保証 (体の中心からこの幅は必ず画面に入れる)。
const HARD_MARGIN_PX = 70
// 主人公からこの距離までの予告の帯・飛び道具は、 画面に入るよう引く。
const THREAT_REACH_PX = 320
const ZOOM_OUT_RATE = 6    // 引く速さ (/秒)。 予告が画面外に出ないよう速く
const ZOOM_IN_RATE = 1.5   // 寄る速さ (/秒)。 ゆっくり = 攻撃のたびに画面が脈打たない
const CENTER_RATE = 5      // 横の追従の速さ (/秒)
const CENTER_DEADZONE_PX = 24
// 案C: 上へ出る物の上に取る余白。
const TOP_MARGIN_PX = 16
// 案C: 名乗り・決着で等倍へ戻る速さ (/秒)。 予告と違って急がない = 場面の切り替わりとして引く。
const SCENE_OUT_RATE = 2

export type CamZoomView = { x: number; y: number; w: number; h: number; z: number }
export type CamZoomInput = {
  playerX: number         // 画面座標 (世界 x − カメラ x)
  bossX: number | null
  threats: { lo: number; hi: number }[]  // 画面座標の予告の帯・飛び道具の範囲
  // 案C (無ければ前の寄せ方と同じ):
  wide?: boolean          // 名乗り・決着 = 等倍で部屋全体を見せる
  topY?: number | null    // 映したい一番上の y (迫撃砲の弾など)。 入るところまで引く
  bossTopY?: number | null // ボスの頭の y。 補間の途中でも必ず入れる
  pullBack?: boolean      // 大きく動く技の間 = 等倍まで速く引く (src/awoken_cam_pull.ts・10-27)
  bossHardMarginPx?: number // ボスの体が HARD_MARGIN_PX より横に広い時の最後の保証 (覚醒獣・11-01)
  tune?: CamZoomTune      // 覚醒獣の普段の寄せ (11-01・AWOKEN_CAM_ROOMY)。 無ければ他のボスと同じ数
}

// 覚醒獣の普段の寄せ (2026-11-01 作者の答え cam=roomy で配布版の既定・前の版は開発版 ?awokenCam=old)。
//   作者 11-01「カメラの感じがもう少し自然になると良いな。なんか今ぎりぎりのところを映してる感じがあるというか」。
//   前の版は二人の外に 150px だけ余白を取って 1.3 倍まで寄せ、 入りきらない分だけ引く = 二人がいつも画面の縁の近くに来た。
//   roomy = 余白を広げて早めに引く (寄せの上限 1.3 は同じ)。 覚醒獣の時だけ render.ts が渡す。
export type CamZoomTune = { zMax: number; pairMarginPx: number; hardMarginPx: number; bossHardMarginPx: number; centerRate: number; centerDeadzonePx: number }
export const AWOKEN_CAM_ROOMY: CamZoomTune = { zMax: 1.3, pairMarginPx: 250, hardMarginPx: 120, bossHardMarginPx: 240, centerRate: 5, centerDeadzonePx: 24 }
let awokenCamTune: CamZoomTune | undefined = AWOKEN_CAM_ROOMY
/** 開発版 ?awokenCam=old (main.ts) で前の版。 それ以外は配布版の既定。 */
export function setAwokenCamTune(name: string | null): void {
  awokenCamTune = name === 'old' ? undefined : AWOKEN_CAM_ROOMY
  settled = false
}
export function currentAwokenCamTune(): CamZoomTune | undefined { return awokenCamTune }

let base: number = CAM_ZOOM_DEFAULT
let fit = true
let scene = true
let settled = false
let zNow = 1
let cNow = ARENA.width / 2
let lastTime = 0

export function setCamZoom(z: number): number {
  base = Number.isFinite(z) ? Math.max(1, Math.min(2, z)) : 1
  settled = false
  return base
}
export function camZoomBase(): number { return base }
export function setCamZoomFit(on: boolean): void { fit = on; settled = false }
export function camZoomFit(): boolean { return fit }
export function setCamZoomScene(on: boolean): void { scene = on; settled = false }
export function camZoomScene(): boolean { return scene }
export function cycleCamZoom(): number {
  const i = CAM_ZOOM_LEVELS.findIndex(v => Math.abs(v - base) < 1e-6)
  return setCamZoom(CAM_ZOOM_LEVELS[(i + 1) % CAM_ZOOM_LEVELS.length]!)
}
export function readCamZoomFromUrl(search: string): void {
  const q = new URLSearchParams(search)
  const z = q.get('camZoom')
  if (z !== null) setCamZoom(Number(z))
  if (q.get('camZoomFit') === '0') setCamZoomFit(false)
  if (q.get('camZoomScene') === '0') setCamZoomScene(false)
  setAwokenCamTune(q.get('awokenCam'))
}

// y を画面に入れられる一番大きい倍率 (床の高さを保つので、 切り取りの上端は floorY − floorY/z)。
export function camZoomMaxForTop(y: number): number {
  const F = ARENA.floorY
  if (y >= F) return Infinity
  if (y <= 0) return 1
  return F / (F - y)
}

// 寄せの目標 (倍率と横の中心)。 純関数 = テスト対象。
export function camZoomTarget(input: CamZoomInput, zBase: number, fitOn: boolean, sceneOn = true): { z: number; c: number; hardZ: number; hardLo: number; hardHi: number; wide: boolean } {
  const W = ARENA.width
  const xs = input.bossX === null ? [input.playerX] : [input.playerX, input.bossX]
  const pLo = Math.min(...xs), pHi = Math.max(...xs)
  const tune = input.tune
  const pairM = tune?.pairMarginPx ?? PAIR_MARGIN_PX
  if (tune) zBase = Math.min(zBase, tune.zMax)
  let lo = pLo - pairM, hi = pHi + pairM
  for (const t of input.threats) {
    const a = Math.max(t.lo, input.playerX - THREAT_REACH_PX)
    const b = Math.min(t.hi, input.playerX + THREAT_REACH_PX)
    if (a > b) continue
    lo = Math.min(lo, a); hi = Math.max(hi, b)
  }
  // 体の大きいボス (覚醒獣) は、 跳んだ先の壁際で頭が画面の端で切れた (11-01・10-29 作者「壁の向こう側に体がなくなって」と同じ崩れ)。 ボスの側だけ保証の幅を広げる。
  const hardM = tune?.hardMarginPx ?? HARD_MARGIN_PX
  const bossM = Math.max(hardM, input.bossHardMarginPx ?? 0)
  const mLo = input.bossX !== null && input.bossX <= input.playerX ? bossM : hardM
  const mHi = input.bossX !== null && input.bossX > input.playerX ? bossM : hardM
  const hardLo = pLo - mLo, hardHi = pHi + mHi
  let hardZ = fitOn ? Math.max(1, Math.min(zBase, W / Math.max(1, hardHi - hardLo))) : zBase
  let z = fitOn ? Math.max(1, Math.min(zBase, W / Math.max(1, hi - lo))) : zBase
  const wide = sceneOn && input.wide === true
  if (sceneOn) {
    const tops = [input.topY, input.bossTopY].filter((y): y is number => typeof y === 'number' && Number.isFinite(y))
    if (tops.length) z = Math.max(1, Math.min(z, camZoomMaxForTop(Math.min(...tops) - TOP_MARGIN_PX)))
    if (typeof input.bossTopY === 'number' && Number.isFinite(input.bossTopY)) hardZ = Math.max(1, Math.min(hardZ, camZoomMaxForTop(input.bossTopY)))
    if (wide || input.pullBack === true) z = 1
  }
  return { z, c: (lo + hi) / 2, hardZ, hardLo, hardHi, wide }
}

// 倍率と中心から、 画面のどこを切り取るか。 床 (floorY) の画面の高さは変えない。
export function camZoomRect(z: number, c: number): CamZoomView {
  const W = ARENA.width, H = ARENA.height
  const w = W / z, h = H / z
  const x = Math.max(0, Math.min(W - w, c - w / 2))
  const y = Math.max(0, Math.min(H - h, ARENA.floorY - ARENA.floorY / z))
  return { x, y, w, h, z }
}

const IDENTITY: CamZoomView = { x: 0, y: 0, w: ARENA.width, h: ARENA.height, z: 1 }
let view: CamZoomView = IDENTITY

// 毎フレーム 1 回 (draw の頭)。 active=false (戦い以外の画面) なら等倍に戻し、 次に戦いへ入った最初のコマは補間しない。
export function updateCamZoom(input: CamZoomInput | null, time: number): CamZoomView {
  if (base <= 1 || !input) { settled = false; view = IDENTITY; return view }
  const t = camZoomTarget(input, base, fit, scene)
  if (!settled) {
    zNow = t.z; cNow = t.c; settled = true; lastTime = time
  } else {
    const dt = Math.max(0, Math.min(0.1, time - lastTime))
    lastTime = time
    const rate = t.z < zNow ? (t.wide ? SCENE_OUT_RATE : ZOOM_OUT_RATE) : ZOOM_IN_RATE
    zNow += (t.z - zNow) * (1 - Math.exp(-rate * dt))
    const err = t.c - cNow
    const over = Math.abs(err) - (input.tune?.centerDeadzonePx ?? CENTER_DEADZONE_PX)
    if (over > 0) cNow += Math.sign(err) * over * (1 - Math.exp(-(input.tune?.centerRate ?? CENTER_RATE) * dt))
  }
  // 補間が追いつかない間も、 二人の体は必ず画面に入れる。
  const z = Math.min(zNow, t.hardZ)
  let r = camZoomRect(z, cNow)
  if (t.hardLo < r.x) r = camZoomRect(z, t.hardLo + r.w / 2)
  else if (t.hardHi > r.x + r.w) r = camZoomRect(z, t.hardHi - r.w / 2)
  view = r
  return view
}

export function camZoomView(): CamZoomView { return view }

// 拡大前の画面座標 → 拡大後の画面座標。 世界の層より後に描く画面の効果 (閃光の中心など) に使う。
export function camZoomX(x: number): number { return (x - view.x) * view.z }
export function camZoomY(y: number): number { return (y - view.y) * view.z }
// 今の描画変換に寄せを掛ける (拡大前の画面座標で描く物を、 拡大後の位置と大きさへ)。
export function applyCamZoomTransform(ctx: CanvasRenderingContext2D): void {
  if (view.z === 1) return
  ctx.scale(view.z, view.z)
  ctx.translate(-view.x, -view.y)
}

let scratch: HTMLCanvasElement | null = null
// 描き終えた世界の層を切り取って、 画面いっぱいに拡大し直す。
export function blitCamZoom(ctx: CanvasRenderingContext2D): void {
  if (view.z === 1 || typeof document === 'undefined') return
  const cv = ctx.canvas
  if (!cv || cv.width <= 0) return
  const s = cv.width / ARENA.width
  const sx = Math.floor(view.x * s), sy = Math.floor(view.y * s)
  const sw = Math.max(1, Math.ceil(view.w * s)), sh = Math.max(1, Math.ceil(view.h * s))
  if (!scratch) scratch = document.createElement('canvas')
  if (scratch.width !== cv.width || scratch.height !== cv.height) { scratch.width = cv.width; scratch.height = cv.height }
  const sctx = scratch.getContext('2d')
  if (!sctx) return
  sctx.setTransform(1, 0, 0, 1, 0, 0)
  sctx.clearRect(0, 0, sw, sh)
  sctx.drawImage(cv, sx, sy, sw, sh, 0, 0, sw, sh)
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, cv.width, cv.height)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(scratch, 0, 0, sw, sh, 0, 0, cv.width, cv.height)
  ctx.restore()
}
